import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  ChevronDown,
  Calendar,
  User,
  ShoppingBag,
  Receipt,
  Wallet,
  FileText,
  CheckCircle,
  XCircle,
  Search,
  BarChart3,
  PieChart,
  TrendingUp,
  Download as DownloadIcon,
  Printer,
  X,
  IndianRupee,
  CreditCard,
  Smartphone,
  Building,
  Landmark,
  Clock,
  CalendarDays,
  FilterX,
  MoreVertical,
  Shield,
  AlertCircle,
  Upload,
  Save,
  File,
  Bell,
  Info,
  DollarSign,
  Percent,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import ExpenseForm from './ExpenseForm';
import ExpenseFilters from './ExpenseFilters';
import ExpenseCharts from './ExpenseCharts';
import {
  fetchExpenses as fetchExpensesApi,
  createExpense as createExpenseApi,
  updateExpense as updateExpenseApi,
  deleteExpense as deleteExpenseApi,
  settlePayment as settlePaymentApi,
} from '../../api';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    subCategory: '',
    paymentMode: '',
    startDate: '',
    endDate: '',
    status: '',
    search: '',
    vendorName: '',
    staffName: '',
    amountRange: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    avgExpense: 0,
    count: 0
  });
  const [notifications, setNotifications] = useState([]);

  // Load expenses from API (used on mount and after add/update/settle)
  const refetchExpenses = React.useCallback(async () => {
    try {
      const list = await fetchExpensesApi().catch((err) => {
        console.error('Failed to load expenses', err);
        return [];
      });
      setExpenses(Array.isArray(list) ? list : []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const list = await fetchExpensesApi().catch((err) => {
          console.error('Failed to load expenses', err);
          return [];
        });
        if (!cancelled) {
          setExpenses(Array.isArray(list) ? list : []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Calculate stats
  useEffect(() => {
    const calculateStats = (expensesList) => {
      const total = expensesList.reduce((sum, exp) => sum + parseFloat(exp.totalAmount || 0), 0);
      const paid = expensesList.reduce((sum, exp) => sum + parseFloat(exp.paidAmount || 0), 0);
      const pending = expensesList.reduce((sum, exp) => sum + parseFloat(exp.pendingAmount || 0), 0);
      const count = expensesList.length;
      const avgExpense = count > 0 ? total / count : 0;

      setStats({
        total,
        paid,
        pending,
        avgExpense,
        count
      });
    };

    calculateStats(filteredExpenses);
  }, [filteredExpenses]);

  // Apply filters
  useEffect(() => {
    let result = [...expenses];

    if (filters.category) {
      result = result.filter(exp => exp.category === filters.category);
    }

    if (filters.subCategory) {
      result = result.filter(exp => exp.subCategory === filters.subCategory);
    }

    if (filters.paymentMode) {
      result = result.filter(exp => exp.paymentMode === filters.paymentMode);
    }

    if (filters.startDate && filters.endDate) {
      result = result.filter(exp => {
        const expDate = new Date(exp.date);
        const start = new Date(filters.startDate);
        const end = new Date(filters.endDate);
        return expDate >= start && expDate <= end;
      });
    }

    if (filters.status) {
      result = result.filter(exp => exp.status === filters.status);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(exp => 
        exp.description?.toLowerCase().includes(searchLower) ||
        exp.vendorName?.toLowerCase().includes(searchLower) ||
        exp.staffName?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.vendorName) {
      result = result.filter(exp => exp.vendorName === filters.vendorName);
    }

    if (filters.staffName) {
      result = result.filter(exp => exp.staffName === filters.staffName);
    }

    if (filters.amountRange) {
      const ranges = {
        '0-5000': { min: 0, max: 5000 },
        '5000-20000': { min: 5000, max: 20000 },
        '20000-50000': { min: 20000, max: 50000 },
        '50000+': { min: 50000, max: Infinity }
      };
      const range = ranges[filters.amountRange];
      if (range) {
        result = result.filter(exp => parseFloat(exp.totalAmount) >= range.min && parseFloat(exp.totalAmount) <= range.max);
      }
    }

    setFilteredExpenses(result);
  }, [filters, expenses]);

  const handleAddExpense = async (newExpense) => {
    try {
      await createExpenseApi(newExpense);
      setShowForm(false);
      addNotification('success', 'Expense added successfully!');
      await refetchExpenses();
    } catch (err) {
      console.error('Error creating expense', err);
      addNotification('error', err.response?.data?.message || err.message || 'Failed to add expense');
    }
  };

  const handleUpdateExpense = async (updatedExpense) => {
    const id = updatedExpense.id;
    if (!id) return;
    try {
      await updateExpenseApi(id, updatedExpense);
      setSelectedExpense(null);
      setShowForm(false);
      addNotification('success', 'Expense updated successfully!');
      await refetchExpenses();
    } catch (err) {
      console.error('Error updating expense', err);
      addNotification('error', err.response?.data?.message || err.message || 'Failed to update expense');
    }
  };

  const handleSettlePayment = async (expenseId, settlementType, amount) => {
    const expense = expenses.find((exp) => exp.id === expenseId);
    if (!expense) return;

    const settlementAmount = parseFloat(amount);
    if (!Number.isFinite(settlementAmount) || settlementAmount <= 0) return;

    try {
      const paymentMode = (expense.paymentMode ?? expense.payment_mode ?? 'cash').toString().trim() || 'cash';
      await settlePaymentApi(expenseId, { amount: settlementAmount, payment_mode: paymentMode });
      addNotification('success', `Payment settled successfully! ₹${settlementAmount.toLocaleString()} paid.`);
      await refetchExpenses();
    } catch (err) {
      console.error('Error settling payment', err);
      const msg = err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Failed to settle payment';
      addNotification('error', Array.isArray(msg) ? msg.join(', ') : msg);
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await deleteExpenseApi(id);
      addNotification('info', 'Expense deleted successfully!');
      await refetchExpenses();
    } catch (err) {
      console.error('Error deleting expense', err);
      addNotification('error', err.response?.data?.message || err.message || 'Failed to delete expense');
    }
  };

  const handleViewDetails = (expense) => {
    setSelectedExpense(expense);
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  const handleResetFilters = () => {
    setFilters({
      category: '',
      subCategory: '',
      paymentMode: '',
      startDate: '',
      endDate: '',
      status: '',
      search: '',
      vendorName: '',
      staffName: '',
      amountRange: ''
    });
    addNotification('info', 'All filters cleared!');
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredExpenses.map(exp => ({
        'Date': exp.date,
        'Category': exp.category.replace('_', ' '),
        'Sub Category': exp.subCategory,
        'Description': exp.description,
        'Vendor': exp.vendorName || '',
        'Staff': exp.staffName || '',
        'Payment Mode': getPaymentMode(exp.paymentMode),
        'Total Amount': exp.totalAmount,
        'Paid Amount': exp.paidAmount,
        'Pending Amount': exp.pendingAmount,
        'Status': exp.status,
        'Transactions': exp.transactions?.length || 0
      }))
    );
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Expenses');
    XLSX.writeFile(workbook, `expenses_${new Date().toISOString().split('T')[0]}.xlsx`);
    addNotification('success', 'Data exported to Excel successfully!');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text('Expense Report', 105, 20, null, null, 'center');
    
    // Date
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 105, 30, null, null, 'center');
    
    // Stats
    doc.setFontSize(12);
    doc.setTextColor(40, 40, 40);
    doc.text(`Total Expenses: ₹${stats.total.toLocaleString()}`, 20, 45);
    doc.text(`Total Paid: ₹${stats.paid.toLocaleString()}`, 20, 55);
    doc.text(`Total Pending: ₹${stats.pending.toLocaleString()}`, 20, 65);
    doc.text(`Total Records: ${stats.count}`, 20, 75);
    
    // Table header
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(22, 163, 74);
    doc.rect(20, 85, 170, 10, 'F');
    doc.text('Date', 25, 91);
    doc.text('Category', 50, 91);
    doc.text('Description', 80, 91);
    doc.text('Amount', 140, 91);
    doc.text('Status', 170, 91);
    
    // Table data
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    let y = 100;
    filteredExpenses.forEach((exp, index) => {
      if (y > 280) {
        doc.addPage();
        y = 20;
      }
      
      doc.text(exp.date, 25, y);
      doc.text(exp.category.replace('_', ' ').substring(0, 15), 50, y);
      doc.text(exp.description.substring(0, 20), 80, y);
      doc.text(`₹${parseFloat(exp.totalAmount).toLocaleString()}`, 140, y);
      if (exp.status === 'paid') doc.setTextColor(34, 197, 94);
      else doc.setTextColor(239, 68, 68);
      doc.text(exp.status || 'pending', 170, y);
      doc.setTextColor(40, 40, 40);
      
      y += 7;
    });
    
    doc.save(`expenses_report_${new Date().toISOString().split('T')[0]}.pdf`);
    addNotification('success', 'Data exported to PDF successfully!');
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'shop_expense': return <Building className="w-5 h-5" />;
      case 'purchase_invoice': return <Receipt className="w-5 h-5" />;
      case 'staff_payment': return <User className="w-5 h-5" />;
      case 'others': return <Wallet className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const getPaymentModeIcon = (mode) => {
    switch(mode) {
      case 'cash': return <IndianRupee className="w-4 h-4" />;
      case 'upi': return <Smartphone className="w-4 h-4" />;
      case 'card': return <CreditCard className="w-4 h-4" />;
      case 'bank_transfer': return <Landmark className="w-4 h-4" />;
      case 'net_banking': return <Building className="w-4 h-4" />;
      default: return <Wallet className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-800 border border-green-200',
      pending: 'bg-red-100 text-red-800 border border-red-200'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status]} flex items-center gap-1`}>
        {status === 'paid' ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPaymentMode = (mode) => {
    const modes = {
      cash: 'Cash',
      upi: 'UPI',
      card: 'Card',
      bank_transfer: 'Bank Transfer',
      net_banking: 'Net Banking'
    };
    return modes[mode] || mode;
  };

  const addNotification = (type, message) => {
    const id = Date.now();
    const notification = { id, type, message };
    setNotifications(prev => [...prev, notification]);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      {/* Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`p-4 rounded-xl shadow-lg border transform transition-all duration-300 animate-slideInRight ${
              notification.type === 'success' 
                ? 'bg-green-50 border-green-200 text-green-800' 
                : notification.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}
          >
            <div className="flex items-center gap-3">
              {notification.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : notification.type === 'error' ? (
                <AlertCircle className="w-5 h-5 text-red-600" />
              ) : (
                <Info className="w-5 h-5 text-blue-600" />
              )}
              <span className="font-medium">{notification.message}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              Expense Management
            </h1>
            <p className="text-gray-600 mt-2 flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              Manage all your shop expenses for {currentMonth}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right">
              <p className="text-sm text-gray-500">Total Records</p>
              <p className="text-2xl font-bold text-gray-900">{stats.count}</p>
            </div>
            <div className="relative">
              {expenses.filter(e => e.status === 'pending').length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {expenses.filter(e => e.status === 'pending').length}
                </span>
              )}
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <Wallet className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 transform hover:-translate-y-1 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium mb-1">Total Expenses</p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{stats.total.toLocaleString()}
              </p>
              <div className="flex items-center gap-1 mt-1">
                <ArrowUpRight className="w-4 h-4 text-green-500" />
                <p className="text-xs text-gray-500">This month</p>
              </div>
            </div>
            <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
              <Wallet className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 transform hover:-translate-y-1 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium mb-1">Pending Amount</p>
              <p className="text-2xl font-bold text-red-600">
                ₹{stats.pending.toLocaleString()}
              </p>
              <div className="flex items-center gap-1 mt-1">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <p className="text-xs text-gray-500">Awaiting payment</p>
              </div>
            </div>
            <div className="p-3 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 transform hover:-translate-y-1 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium mb-1">Paid Amount</p>
              <p className="text-2xl font-bold text-green-600">
                ₹{stats.paid.toLocaleString()}
              </p>
              <div className="flex items-center gap-1 mt-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <p className="text-xs text-gray-500">Settled payments</p>
              </div>
            </div>
            <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 transform hover:-translate-y-1 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium mb-1">Average Expense</p>
              <p className="text-2xl font-bold text-blue-600">
                ₹{stats.avgExpense.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
              <div className="flex items-center gap-1 mt-1">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <p className="text-xs text-gray-500">Per transaction</p>
              </div>
            </div>
            <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => {
              setSelectedExpense(null);
              setShowForm(true);
            }}
            className="group relative bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-xl flex items-center gap-3 hover:from-green-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
            <Plus className="w-5 h-5 relative z-10" />
            <span className="font-semibold relative z-10">Add New Expense</span>
          </button>

          <button
            onClick={() => setViewMode(viewMode === 'list' ? 'chart' : 'list')}
            className="group bg-white text-gray-700 px-6 py-3 rounded-xl flex items-center gap-3 hover:bg-gray-50 transition-all border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md"
          >
            {viewMode === 'list' ? (
              <>
                <BarChart3 className="w-5 h-5 text-green-600" />
                <span>View Charts</span>
              </>
            ) : (
              <>
                <FileText className="w-5 h-5 text-blue-600" />
                <span>View List</span>
              </>
            )}
          </button>
        </div>

        <div className="flex gap-3">
          <button
            onClick={exportToExcel}
            className="group bg-white text-gray-700 px-6 py-3 rounded-xl flex items-center gap-3 hover:bg-gray-50 transition-all border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md"
            title="Export to Excel"
          >
            <DownloadIcon className="w-5 h-5 text-green-600 group-hover:text-green-700" />
            <span className="hidden md:inline">Excel</span>
          </button>
          <button
            onClick={exportToPDF}
            className="group bg-white text-gray-700 px-6 py-3 rounded-xl flex items-center gap-3 hover:bg-gray-50 transition-all border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md"
            title="Export to PDF"
          >
            <Printer className="w-5 h-5 text-red-600 group-hover:text-red-700" />
            <span className="hidden md:inline">PDF</span>
          </button>
         
        </div>
      </div>

      {/* Filters */}
      <ExpenseFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
        expenses={expenses}
      />

      {/* Content Area */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 px-6">
            <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mb-4" />
            <p className="text-gray-600 font-medium">Loading expenses…</p>
          </div>
        ) : viewMode === 'chart' ? (
          <ExpenseCharts 
            expenses={filteredExpenses} 
            filters={filters}
            stats={stats}
          />
        ) : (
          <>
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Expense Records
                  <span className="ml-2 text-sm text-gray-500 font-normal">
                    ({filteredExpenses.length} records found)
                  </span>
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Filter className="w-4 h-4" />
                  <span>Filtered</span>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Date & Category
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredExpenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50 transition-colors group">
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
                            {getCategoryIcon(expense.category)}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {expense.date}
                            </div>
                            <div className="text-xs text-gray-500 capitalize mt-1">
                              {expense.category.replace('_', ' ')}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {expense.subCategory}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="max-w-xs">
                          <div className="text-sm font-medium text-gray-900 line-clamp-2">
                            {expense.description}
                          </div>
                          {expense.vendorName && (
                            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Vendor: {expense.vendorName}
                            </div>
                          )}
                          {expense.staffName && (
                            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Staff: {expense.staffName}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getPaymentModeIcon(expense.paymentMode)}
                          <span className="text-sm text-gray-700">
                            {getPaymentMode(expense.paymentMode)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="text-sm font-semibold text-gray-900">
                            ₹{parseFloat(expense.totalAmount).toLocaleString()}
                          </div>
                          <div className="text-xs">
                            <span className="text-green-600 font-medium">
                              Paid: ₹{parseFloat(expense.paidAmount).toLocaleString()}
                            </span>
                            {expense.pendingAmount > 0 && (
                              <div className="text-red-600 font-medium mt-1">
                                Due: ₹{parseFloat(expense.pendingAmount).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        {getStatusBadge(expense.status)}
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleViewDetails(expense)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedExpense(expense);
                              setShowForm(true);
                            }}
                            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredExpenses.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <FileText className="w-10 h-10 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-700 mb-2">No expenses found</h4>
                  <p className="text-gray-500 mb-6 max-w-md mx-auto">
                    {Object.values(filters).some(f => f) 
                      ? 'Try changing your filters or'
                      : 'Start by adding your first expense'}
                  </p>
                  <button
                    onClick={() => {
                      setSelectedExpense(null);
                      setShowForm(true);
                    }}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-3 rounded-xl flex items-center gap-3 hover:from-green-600 hover:to-emerald-700 transition-all duration-300 shadow-lg mx-auto"
                  >
                    <Plus className="w-5 h-5" />
                    Add New Expense
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showForm && (
        <div className="ui-modal-overlay">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <ExpenseForm
              expense={selectedExpense}
              onSave={selectedExpense ? handleUpdateExpense : handleAddExpense}
              onCancel={() => {
                setShowForm(false);
                setSelectedExpense(null);
              }}
              onSettlePayment={handleSettlePayment}
            />
          </div>
        </div>
      )}

      {selectedExpense && !showForm && (
        <div className="ui-modal-overlay">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 z-10">
              <div className="flex justify-between items-center p-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Expense Details</h3>
                  <p className="text-gray-500 mt-1">Complete transaction information</p>
                </div>
                <button
                  onClick={() => setSelectedExpense(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500 hover:text-gray-700" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Header Info */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-white to-green-100 flex items-center justify-center shadow-lg">
                      {getCategoryIcon(selectedExpense.category)}
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 capitalize">
                        {selectedExpense.category.replace('_', ' ')}
                      </h4>
                      <p className="text-gray-600 capitalize">{selectedExpense.subCategory}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900">
                      ₹{parseFloat(selectedExpense.totalAmount).toLocaleString()}
                    </div>
                    {getStatusBadge(selectedExpense.status)}
                  </div>
                </div>
              </div>

              {/* Grid Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* Left Column */}
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-xl p-5">
                    <h5 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                      Basic Information
                    </h5>
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 block mb-1">Date</label>
                        <div className="flex items-center gap-2 text-gray-900">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {selectedExpense.date}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 block mb-1">Description</label>
                        <p className="text-gray-900">{selectedExpense.description}</p>
                      </div>
                      {selectedExpense.vendorName && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 block mb-1">Vendor</label>
                          <div className="flex items-center gap-2 text-gray-900">
                            <User className="w-4 h-4 text-gray-400" />
                            {selectedExpense.vendorName}
                          </div>
                        </div>
                      )}
                      {selectedExpense.staffName && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 block mb-1">Staff</label>
                          <div className="flex items-center gap-2 text-gray-900">
                            <User className="w-4 h-4 text-gray-400" />
                            {selectedExpense.staffName}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Information */}
                  <div className="bg-gray-50 rounded-xl p-5">
                    <h5 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                      Payment Information
                    </h5>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Payment Mode</span>
                        <div className="flex items-center gap-2">
                          {getPaymentModeIcon(selectedExpense.paymentMode)}
                          <span className="font-medium text-gray-900">
                            {getPaymentMode(selectedExpense.paymentMode)}
                          </span>
                        </div>
                      </div>
                      <div className="h-px bg-gray-200"></div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Total Amount</span>
                        <span className="font-bold text-lg text-gray-900">
                          ₹{parseFloat(selectedExpense.totalAmount).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Paid Amount</span>
                        <span className="font-semibold text-green-600">
                          ₹{parseFloat(selectedExpense.paidAmount).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Pending Amount</span>
                        <span className={`font-semibold ${selectedExpense.pendingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          ₹{parseFloat(selectedExpense.pendingAmount).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Payment & settlement history */}
                <div className="bg-gray-50 rounded-xl p-5">
                  <h5 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                    Payment & settlement history
                  </h5>
                  <div className="space-y-3">
                    {selectedExpense.transactions && selectedExpense.transactions.length > 0 ? (
                      [...selectedExpense.transactions]
                        .sort((a, b) => {
                          const dA = a.date && a.date !== '—' ? `${a.date}T${a.time || '00:00:00'}` : '';
                          const dB = b.date && b.date !== '—' ? `${b.date}T${b.time || '00:00:00'}` : '';
                          if (!dA) return 1;
                          if (!dB) return -1;
                          return (new Date(dB) - new Date(dA)) || 0;
                        })
                        .map((transaction, index) => (
                          <div key={`${transaction.date}-${transaction.time}-${transaction.amount}-${index}`} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-1">
                                  <Calendar className="w-3.5 h-3.5" />
                                  Date & time
                                </div>
                                <div className="flex flex-wrap items-baseline gap-2 mb-1">
                                  <span className="font-semibold text-gray-900">{transaction.date}</span>
                                  <span className="text-sm text-gray-600">{transaction.time}</span>
                                </div>
                                <div className="text-sm text-gray-600 mt-1">{transaction.description}</div>
                                <div className="text-xs text-gray-500 flex items-center gap-2 mt-2">
                                  {getPaymentModeIcon(transaction.paymentMode)}
                                  {getPaymentMode(transaction.paymentMode)}
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="font-bold text-green-600">
                                  ₹{(parseFloat(transaction.amount) || 0).toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500 capitalize">{transaction.type === 'payment' ? 'Payment' : transaction.type}</div>
                              </div>
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        No payment or settlement history yet
                      </div>
                    )}
                  </div>

                  {/* Settlement Options (if pending) */}
                  {selectedExpense.pendingAmount > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h6 className="text-sm font-semibold text-gray-700 mb-3">Settle Payment</h6>
                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to settle the full pending amount of ₹${selectedExpense.pendingAmount.toLocaleString()}?`)) {
                              handleSettlePayment(selectedExpense.id, 'full', selectedExpense.pendingAmount);
                              setSelectedExpense(null);
                            }
                          }}
                          className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-4 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all text-sm font-medium"
                        >
                          Full Settlement
                        </button>
                        <button
                          onClick={() => {
                            setSelectedExpense(null);
                            setTimeout(() => {
                              setSelectedExpense(selectedExpense);
                              setShowForm(true);
                            }, 100);
                          }}
                          className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-600 text-white py-3 px-4 rounded-lg hover:from-blue-600 hover:to-cyan-700 transition-all text-sm font-medium"
                        >
                          Edit & Pay Partial
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Attachments */}
              {selectedExpense.attachments && selectedExpense.attachments.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-5">
                  <h5 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                    Attachments
                  </h5>
                  <div className="flex flex-wrap gap-3">
                    {selectedExpense.attachments.map((attachment, index) => {
                      const name = typeof attachment === 'object' && attachment !== null
                        ? (attachment.file_name || attachment.file_path || attachment.name || 'Attachment')
                        : String(attachment);
                      const key = typeof attachment === 'object' && attachment !== null && attachment.id ? attachment.id : index;
                      return (
                        <div key={key} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-200 hover:border-gray-300 transition-colors">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                            <File className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{name}</div>
                            <div className="text-xs text-gray-500">PDF/Image</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;